<ng-template #teamCell let-metricDataPoint>
  @if (metricDataPoint.user.team; as team) {
    @if (canFollowSupportLinks$ | async) {
      <app-support-link
        linkClass="team-badge"
        [backgroundColor]="teamColor(team.environment, team.id)"
        [environment]="team.environment"
        resource="teams"
        [id]="team.id"
      >
        {{ team.name }}<span class="no-select"> ({{ team.id }})</span>
      </app-support-link>
    } @else {
      <span class="team-badge" [style.background-color]="teamColor(team.environment, team.id)">{{ team.name }}</span>
    }
  } @else {
    -
  }
</ng-template>

<ng-template #environmentCell let-metricDataPoint>
  <app-environment-pill [environment]="metricDataPoint.environment"></app-environment-pill>
</ng-template>

<div class="content">
  @if (dashboardUrl !== null) {
    <a mat-flat-button color="primary" class="status-dashboard-button" [href]="dashboardUrl" target="_blank" rel="noopener noreferrer">
      <mat-icon>open_in_new</mat-icon>
      {{ 'SETTINGS.OPEN_STATUS_DASHBOARD' | translate }}
    </a>
  }
  @let selectedInterval = selectedInterval$ | async;
  @let subscriptionUsage = _subscriptionUsage$ | async;
  <div class="filters">
    <app-time-interval
      [intervalKeys]="(intervalKeys$ | async) ?? baseIntervalKeys"
      [subscriptionPeriodLabel]="subscriptionPeriodLabel$ | async"
      [selectedInterval]="selectedInterval!"
      (update)="updateIntervalKey($event)"
    ></app-time-interval>
  </div>
  @if (subscriptionUsage !== null) {
    <app-subscription-usage-panel [usage]="subscriptionUsage"></app-subscription-usage-panel>
  }
  @let usageSummary = usageSummary$ | async;
  <div class="numeric-data">
    @if (usageSummary === null || usageSummary.isLoading) {
      <app-spinner></app-spinner>
    } @else {
      <div class="metric-card">
        <div class="metric-label">{{ 'METRICS.ANALYTICS_DASHBOARD.TOTAL_HOURS' | translate }}</div>
        <div class="metric-value">{{ usageSummary.duration / 3600 | number: '1.0-0' }}</div>
        <div class="metric-subtext">{{ 'COMMON.TIME_INTERVAL.' + selectedInterval | translate }}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">{{ 'METRICS.ANALYTICS_DASHBOARD.TOTAL_MEETINGS' | translate }}</div>
        <div class="metric-value">{{ usageSummary.count }}</div>
        <div class="metric-subtext">{{ 'COMMON.TIME_INTERVAL.' + selectedInterval | translate }}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">{{ 'METRICS.ANALYTICS_DASHBOARD.ACTIVE_USERS' | translate }}</div>
        <div class="metric-value">{{ usageSummary.activeUserCount }}</div>
        <div class="metric-subtext">{{ 'COMMON.AS_OF_TODAY' | translate }}</div>
      </div>
    }
  </div>
  <div class="usage-tables">
    @let usageStatsSelectedViewOption = usageStatsSelectedView$ | async;
    <div class="table-controls">
      <app-button-toggle
        [options]="usageStatsViewOptions"
        [value]="usageStatsSelectedViewOption"
        (update)="updateSelectedView($event, usageStatsQueryPrefix)"
      ></app-button-toggle>
    </div>
    <div class="panel chart-panel">
      <div class="section-header">
        <div>
          @if (usageStatsSelectedViewOption === 'HOURS') {
            <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.TOTAL_HOURS_BY_TEAM' | translate }}</h3>
          } @else {
            <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.TOTAL_MEETINGS_BY_TEAM' | translate }}</h3>
          }
          <div class="metric-subtext">{{ 'COMMON.TIME_INTERVAL.' + selectedInterval | translate }}</div>
        </div>
      </div>
      @if (usageSummary === null || usageSummary.isLoading) {
        <app-spinner></app-spinner>
      } @else if (chartDataForTeamUsage$ | async; as chartDataForTeamUsage) {
        @if (barChartOptions$ | async; as barChartOptions) {
          <canvas baseChart [data]="chartDataForTeamUsage" [options]="barChartOptions" [plugins]="[dataLabelsPlugin]" [type]="'bar'"> </canvas>
        }
      }
    </div>

    <div class="panel table-panel">
      <div class="section-header">
        <div>
          @if (usageStatsSelectedViewOption === 'HOURS') {
            <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.TOTAL_HOURS_BY_USER' | translate }}</h3>
          } @else {
            <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.TOTAL_MEETINGS_BY_USER' | translate }}</h3>
          }

          <div class="metric-subtext">{{ 'COMMON.TIME_INTERVAL.' + selectedInterval | translate }}</div>
        </div>
      </div>

      @if (userStats$ | async; as result) {
        @if (result.isLoading) {
          <app-spinner></app-spinner>
        } @else if (result.isError) {
          <p>{{ 'COMMON.ERROR_LOADING_DATA' | translate }}</p>
        } @else {
          @if (result.data; as userStats) {
            @if (columnDefinitionsForUserStats$ | async; as columns) {
              <app-table
                [source]="userStats"
                [columns]="columns"
                [rowId]="userStatsRowId"
                [selectable]="false"
                [pageSize]="10"
                [syncUrl]="true"
                urlPrefix="userStats"
                tableId="analytics-user-stats"
                csvFilename="user-stats"
                label="METRICS.ANALYTICS_DASHBOARD.USAGE_BY_USER"
                rowNoun="COMMON.USER"
                rowNounPlural="COMMON.USERS"
              />
            }
          }
        }
      }
    </div>
  </div>
  @if (showAudioMetrics) {
    <div class="panel audio-quality-chart-panel">
      <div class="section-header">
        <div>
          <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.MEETING_SCORES' | translate }}</h3>
          <p class="section-subtitle">{{ 'METRICS.ANALYTICS_DASHBOARD.MEETING_SCORES_DESCRIPTION' | translate }}</p>
        </div>
        @if (showMeetingNameToggle) {
          <mat-slide-toggle [checked]="showMeetingLabels$.value" (change)="toggleMeetingLabels()" color="primary">
            {{ 'METRICS.ANALYTICS_DASHBOARD.SHOW_MEETING_NAMES' | translate }}
          </mat-slide-toggle>
        }
      </div>
      @if (meetingScoresChart$ | async; as chart) {
        <div class="audio-quality-chart-container">
          <canvas baseChart [data]="audioQualityChartData" [options]="chart.options" [plugins]="[chart.dotsPlugin]" [type]="'bar'"></canvas>
          @if (chart.legend.length > 0) {
            <div class="chart-legend">
              @for (item of chart.legend; track item.name) {
                <div class="chart-legend-item">
                  <span class="chart-legend-color" [style.backgroundColor]="item.color"></span>
                  <span>{{ item.name }}</span>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
  @if (showMeetingRoomMetrics) {
    <div class="panel meeting-rooms-panel">
      <div class="section-header">
        <div>
          <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.MEETING_ROOMS' | translate }}</h3>
        </div>
      </div>
      @if (meetingRoomMetrics$ | async; as result) {
        @if (result.isLoading) {
          <app-spinner></app-spinner>
        } @else if (result.isError) {
          <p>{{ 'COMMON.ERROR_LOADING_DATA' | translate }}</p>
        } @else {
          @if (result.data; as rooms) {
            @if (rooms.length > 0) {
              <app-table
                [source]="rooms"
                [columns]="meetingRoomColumns"
                [rowId]="meetingRoomRowId"
                [selectable]="false"
                [pageSize]="25"
                [syncUrl]="true"
                urlPrefix="meetingRooms"
                tableId="analytics-meeting-rooms"
                csvFilename="meeting-rooms"
                label="METRICS.ANALYTICS_DASHBOARD.MEETING_ROOMS"
                rowNoun="METRICS.ANALYTICS_DASHBOARD.ROOM"
                rowNounPlural="METRICS.ANALYTICS_DASHBOARD.ROOMS"
              />
            } @else {
              <p class="no-data-message">{{ 'METRICS.ANALYTICS_DASHBOARD.NO_MEETING_ROOMS' | translate }}</p>
            }
          }
        }
      }
    </div>
    <div class="panel audio-quality-chart-panel">
      <div class="section-header">
        <div>
          <h3>{{ 'METRICS.ANALYTICS_DASHBOARD.MEETING_ROOM_SCORES' | translate }}</h3>
        </div>
        <mat-slide-toggle [checked]="showMeetingRoomLabels$.value" (change)="toggleMeetingRoomLabels()" color="primary">
          {{ 'METRICS.ANALYTICS_DASHBOARD.SHOW_MEETING_NAMES' | translate }}
        </mat-slide-toggle>
      </div>
      @if (meetingRoomScoresChart$ | async; as chart) {
        <div class="audio-quality-chart-container">
          <canvas baseChart [data]="audioQualityChartData" [options]="chart.options" [plugins]="[chart.dotsPlugin]" [type]="'bar'"></canvas>
          @if (chart.legend.length > 0) {
            <div class="chart-legend">
              @for (item of chart.legend; track item.name) {
                <div class="chart-legend-item">
                  <span class="chart-legend-color" [style.backgroundColor]="item.color"></span>
                  <span>{{ item.name }}</span>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>
